// src/config.ts
// Central config: tweak SIGNAL_HOST to point at your deployed Worker.

export const SIGNAL_HOST =
  (import.meta.env.VITE_SIGNAL_HOST as string | undefined) ?? 'teslawithme-signal.workers.dev';
export const SIGNAL_PORT = Number(
  (import.meta.env.VITE_SIGNAL_PORT as string | undefined) ?? 443,
);
export const SIGNAL_PATH =
  (import.meta.env.VITE_SIGNAL_PATH as string | undefined) ?? '/peerjs/';
export const SIGNAL_SECURE =
  import.meta.env.VITE_SIGNAL_SECURE !== undefined
    ? import.meta.env.VITE_SIGNAL_SECURE !== 'false'
    : SIGNAL_PORT === 443;

export type MapStyle = 'auto' | 'light' | 'dark' | 'satellite' | 'outdoors';

export interface TilePreset {
  url: string;
  /** Optional labels/reference layer drawn above `url` (e.g. Esri Dark Gray). */
  overlayUrl?: string;
  attribution: string;
  maxZoom: number;
  subdomains?: string;
}

const ESRI = 'Tiles &copy; <a href="https://www.esri.com">Esri</a>';
const OSM = '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors';

export const TILE_PRESETS: Record<Exclude<MapStyle, 'auto'>, TilePreset> = {
  // Carto's public basemap CDN now watermarks every tile with
  // "API KEY REQUIRED". Esri's ArcGIS Online raster tiles don't.
  light: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: `${ESRI} &mdash; Esri, HERE, Garmin, ${OSM}, USGS`,
    maxZoom: 19,
  },
  dark: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    overlayUrl: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
    attribution: `${ESRI} &mdash; Esri, HERE, Garmin, ${OSM}, GIS User Community`,
    maxZoom: 19,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: `${ESRI} &mdash; Source: Esri, Maxar, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN`,
    maxZoom: 19,
  },
  outdoors: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: `${ESRI} &mdash; Esri, HERE, Garmin, ${OSM}, USGS`,
    maxZoom: 19,
  },
};

export const MAP_STYLE_STORAGE_KEY = 'teslawithme:mapStyle';

// Legacy single-tile exports kept for any code that still imports them directly.
export const MAP_TILE_URL = TILE_PRESETS.light.url;
export const MAP_TILE_ATTRIBUTION = TILE_PRESETS.light.attribution;
export const MAP_TILE_MAX_ZOOM = 19;

// Location broadcast throttle: don't send more often than this (ms)
export const LOCATION_THROTTLE_MS = 10_000;

// App version for debugging
export const APP_VERSION = '0.1.0';
